import { Spin, message } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../Contexts/AuthContext";
import axios from "../../config/axios";
import { GOOGLE_CLIENT_ID, isGoogleAuthConfigured, loadGoogleIdentity } from "../../utils/googleIdentity";
import styles from "./GoogleLoginButton.module.scss";

// Renders Google's own "Sign in with Google" button and exchanges the credential
// it produces for a Desi Fresh session.
//
// Google hands us a signed ID token in the browser; the backend verifies that
// signature and either logs the matching customer in or creates the account. The
// same component covers sign-in and sign-up - Google cannot tell us which one it
// is, and the backend does not need to be told.
const GoogleLoginButton = ({ dfCard = false, onSuccess, text = "signin_with" }) => {
    const buttonRef = useRef(null);
    const { setUser, setIsAuthenticated } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [unavailable, setUnavailable] = useState(false);

    // read through a ref so re-renders of the parent never re-init the button
    const handlersRef = useRef({ dfCard, onSuccess });
    handlersRef.current = { dfCard, onSuccess };

    useEffect(() => {
        if (!isGoogleAuthConfigured()) {
            console.warn("REACT_APP_GOOGLE_CLIENT_ID is not set - the Google sign-in button is hidden.");
            setUnavailable(true);
            return;
        }

        let cancelled = false;

        const handleCredential = async (response) => {
            if (!response?.credential) {
                message.error("Google sign-in was cancelled.");
                return;
            }

            try {
                setLoading(true);
                const { data } = await axios.post("/users/google-login", {
                    credential: response.credential,
                    df_card: handlersRef.current.dfCard ? true : null,
                });

                localStorage.setItem("device_id", data.access_token);
                let userData = data.user;
                userData.wishlist = !userData.wishlist ? [] : userData.wishlist;

                setIsAuthenticated(true);
                setUser(userData);
                message.success(data.created ? "Welcome to Desi Fresh!" : "Successfully logged in!");
                handlersRef.current.onSuccess?.(data);
            } catch (error) {
                message.error(error.response?.data?.msg || "Google sign-in failed. Please try again.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadGoogleIdentity()
            .then((googleId) => {
                if (cancelled || !buttonRef.current) return;

                googleId.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleCredential,
                });

                googleId.renderButton(buttonRef.current, {
                    theme: "outline",
                    size: "large",
                    text,
                    shape: "rectangular",
                    logo_alignment: "center",
                    width: buttonRef.current.offsetWidth || undefined,
                });
            })
            .catch(() => {
                if (!cancelled) setUnavailable(true);
            });

        return () => {
            cancelled = true;
        };
        // `text` is a static per-usage label; the callback reads live values off
        // handlersRef, so the button only ever needs to be initialised once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (unavailable) return null;

    return (
        <div className={styles.google_button_wrapper}>
            <div ref={buttonRef} className={styles.google_button} />
            {loading && (
                <div className={styles.google_button_overlay}>
                    <Spin size="small" />
                    <span>Signing you in...</span>
                </div>
            )}
        </div>
    );
};

export default GoogleLoginButton;
